import { googleDriveApi } from '../services/api';

export type GoogleDrivePickerFile = {
  id: string;
  name: string;
  mimeType: string;
  url?: string;
};

let pickerLoadPromise: Promise<void> | null = null;

const loadGooglePicker = async (): Promise<void> => {
  if ((window as any).google?.picker) return;
  if (pickerLoadPromise) return pickerLoadPromise;
  pickerLoadPromise = new Promise<void>((resolve, reject) => {
    const finishLoading = () => {
      (window as any).gapi.load('picker', {
        callback: resolve,
        ontimeout: () => reject(new Error('Google Picker timed out.')),
        timeout: 10000,
      });
    };
    const existing = document.querySelector('script[data-google-picker="1"]') as HTMLScriptElement | null;
    if (existing) {
      if ((window as any).gapi) finishLoading();
      else {
        existing.addEventListener('load', finishLoading, { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load Google Picker.')), { once: true });
      }
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.defer = true;
    script.setAttribute('data-google-picker', '1');
    script.onload = finishLoading;
    script.onerror = () => reject(new Error('Failed to load Google Picker.'));
    document.body.appendChild(script);
  }).catch((error) => {
    pickerLoadPromise = null;
    throw error;
  });
  return pickerLoadPromise;
};

export const pickGoogleDriveFile = async (): Promise<GoogleDrivePickerFile | null> => {
  const developerKey = (process.env.REACT_APP_GOOGLE_PICKER_API_KEY || '').trim();
  if (!developerKey) throw new Error('Google Drive Picker is not configured.');
  await loadGooglePicker();
  const tokenResponse = await googleDriveApi.accessToken();
  const accessToken = String(tokenResponse?.data?.access_token || '');
  const pickerAppId = String(tokenResponse?.data?.picker_app_id || '');
  const lessonResourcesFolderId = String(tokenResponse?.data?.lesson_resources_folder_id || '');
  if (!accessToken) throw new Error('Google Drive did not return an access token.');
  if (!/^\d+$/.test(pickerAppId)) throw new Error('Google Picker project ID is not configured.');
  return new Promise((resolve, reject) => {
    const pickerApi = (window as any).google.picker;
    const documentsView = new pickerApi.DocsView(pickerApi.ViewId.DOCS);
    if (/^[A-Za-z0-9_-]{10,255}$/.test(lessonResourcesFolderId)) {
      documentsView.setParent(lessonResourcesFolderId);
    }
    documentsView.setIncludeFolders(true);
    documentsView.setSelectFolderEnabled(false);
    const picker = new pickerApi.PickerBuilder()
      .setDeveloperKey(developerKey)
      .setOAuthToken(accessToken)
      .setAppId(pickerAppId)
      .setOrigin(window.location.origin)
      .addView(documentsView)
      .setCallback((data: any) => {
        if (data.action === pickerApi.Action.PICKED) {
          const doc = data.docs?.[0];
          resolve(doc?.id ? {
            id: String(doc.id),
            name: String(doc.name || 'Google Drive file'),
            mimeType: String(doc.mimeType || ''),
            url: doc.url ? String(doc.url) : undefined,
          } : null);
        } else if (data.action === pickerApi.Action.CANCEL) {
          resolve(null);
        } else if (data.action === pickerApi.Action.ERROR) {
          reject(new Error(String(data.message || 'Google Picker could not be opened.')));
        }
      })
      .build();
    picker.setVisible(true);
  });
};
